// Max video size for the LEGACY upload path, where the whole file streams through the API (guides and
// recipes, which post the video inside the entity's own multipart save). Bounded by the server's
// spring.servlet.multipart.max-file-size — raising it past that just moves the failure from a friendly
// message to a container-level rejection. Override via REACT_APP_MAX_VIDEO_UPLOAD_MB.
export const MAX_VIDEO_UPLOAD_MB = Number(process.env.REACT_APP_MAX_VIDEO_UPLOAD_MB) || 150;

// Max video size for the DIRECT-TO-S3 path (exercises, progress videos). The bytes never reach the API,
// so the servlet limit doesn't apply and the ceiling is the server's progress.media.upload
// .max-upload-bytes instead. Override via REACT_APP_MAX_DIRECT_VIDEO_UPLOAD_MB.
export const MAX_DIRECT_VIDEO_UPLOAD_MB = Number(process.env.REACT_APP_MAX_DIRECT_VIDEO_UPLOAD_MB) || 1024;

// User-facing message shown when a selected video is too large. Defaults to the legacy limit so existing
// call sites on that path keep their copy; the direct path passes MAX_DIRECT_VIDEO_UPLOAD_MB.
export const getVideoTooLargeMessage = (limitMb: number = MAX_VIDEO_UPLOAD_MB): string =>
    `הסרטון גדול מדי. הגודל המקסימלי המותר הוא ${limitMb}MB`;

// YouTube validation - kept for backward compatibility
export const isValidYouTubeUrl = (url: string | null | undefined): boolean => {
    if (!url || !url.trim()) return true; // Empty is valid (optional field)
    const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^#&?]*)/,
        /youtu\.be\/([^#&?]*)/,
        /youtube\.com\/embed\/([^#&?]*)/,
        /youtube\.com\/shorts\/([^#&?]*)/,
        /m\.youtube\.com\/watch\?v=([^#&?]*)/
    ];
    return patterns.some(pattern => pattern.test(url));
};

export const extractYouTubeId = (url: string | null | undefined): string | null => {
    if (!url) return null;

    const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^#&?]*)/,
        /youtu\.be\/([^#&?]*)/,
        /youtube\.com\/embed\/([^#&?]*)/,
        /youtube\.com\/shorts\/([^#&?]*)/,
        /youtu\.be\/([^?]*)/,
        /m\.youtube\.com\/watch\?v=([^#&?]*)/
    ];

    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1]) {
            return match[1];
        }
    }

    return null;
};

// Get YouTube thumbnail URL from video URL
export const getYouTubeThumbnailUrl = (url: string | null | undefined): string | null => {
    const videoId = extractYouTubeId(url);
    if (!videoId) return null;
    // Use maxresdefault for best quality, falls back to hqdefault if not available
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
};

// Generic http/https URL validation. Empty is valid (these are optional fields).
export const isValidHttpUrl = (url: string | null | undefined): boolean => {
    if (!url || !url.trim()) return true; // Empty is valid (optional field)
    try {
        const parsed = new URL(url);
        return ['http:', 'https:'].includes(parsed.protocol);
    } catch {
        return false;
    }
};

// Universal video URL validation - accepts any valid http/https URL
export const isValidVideoUrl = isValidHttpUrl;

export type VideoType = 'youtube' | 's3' | 'file' | 'hls' | 'other';

// Detect video source type
export const getVideoType = (url: string | null | undefined): VideoType | null => {
    if (!url) return null;
    if (isValidYouTubeUrl(url)) return 'youtube';
    if (url.includes('.s3.') || url.includes('amazonaws.com') || url.includes('cloudfront.net')) return 's3';
    if (/\.(mp4|webm|ogg|mov|avi|mkv)(\?|$)/i.test(url)) return 'file';
    if (/\.m3u8(\?|$)/i.test(url)) return 'hls';
    return 'other';
};
